import { Component, Inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AllowedDeductionType, CompaniesService, Company, CompanySettings } from 'src/app/core/companies.service';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { FormService } from 'src/app/core/form.service';

@Component({
  selector: 'app-companies-settings-dialog',
  templateUrl: './companies-settings-dialog.component.html',
  styleUrls: ['./companies-settings-dialog.component.scss'],
})
export class CompaniesSettingsDialogComponent {
  company?: Company;
  company_settings_form_group: FormGroup = this.form_service.company_settings_form_group;
  is_company_edit_running: boolean = false;
  allowed_deduction_types = AllowedDeductionType;

  constructor(
    private dialog_ref: MatDialogRef<CompaniesSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private form_service: FormService,
    private companies_service: CompaniesService,
    private error_service: ErrorService,
  ) {
    this.company = data.company;
  }

  ngOnInit(): void {
    this.company_settings_form_group
      .get(this.form_service.is_store_payment_method_enabled_label)
      ?.setValue(this.company?.settings?.is_store_payment_method_enabled);

    this.company_settings_form_group
      .get(this.form_service.is_iban_refund_enabled_label)
      ?.setValue(this.company?.settings?.is_iban_refund_enabled);

    this.company_settings_form_group
      .get(this.form_service.is_ext_order_id_enabled_label)
      ?.setValue(this.company?.settings?.is_ext_order_id_enabled);

    this.company_settings_form_group
      .get(this.form_service.is_tax_deduction_enabled_label)
      ?.setValue(this.company?.settings?.is_tax_deduction_enabled);

    this.company_settings_form_group
      .get(this.form_service.is_tax_date_and_number_enabled_label)
      ?.setValue(this.company?.settings?.is_tax_date_and_number_enabled);

    this.company_settings_form_group
      .get(this.form_service.tax_deduction_type_label)
      ?.setValue(this.company?.settings?.allowed_deduction_types);

    this.company_settings_form_group
      .get(this.form_service.is_custom_domains_enabled_label)
      ?.setValue(this.company?.settings?.is_custom_domains_enabled);

    this.company_settings_form_group
      .get(this.form_service.is_fourthline_onboarding_enabled_label)
      ?.setValue(this.company?.settings?.is_fourthline_onboarding_enabled);

    this.company_settings_form_group
      .get(this.form_service.is_prepaid_cards_blocked_label)
      ?.setValue(this.company?.settings?.is_prepaid_cards_blocked);

    this.company_settings_form_group
      .get(this.form_service.is_prepaid_cards_blocked_on_long_label)
      ?.setValue(this.company?.settings?.is_prepaid_cards_blocked_on_long);

    this.company_settings_form_group
      .get(this.form_service.is_prepaid_cards_blocked_on_ecommerce_label)
      ?.setValue(this.company?.settings?.is_prepaid_cards_blocked_on_ecommerce);

    if (this.isPrepaidBlockEnabled()) {
      this.company_settings_form_group.get(this.form_service.is_prepaid_cards_blocked_on_long_label)?.disable();
      this.company_settings_form_group.get(this.form_service.is_prepaid_cards_blocked_on_ecommerce_label)?.disable();
    }
  }

  onCloseClick(): void {
    this.dialog_ref.close();
  }

  onConfirmClick(): void {
    const is_store_payment_method_enabled =
      this.company_settings_form_group.get(this.form_service.is_store_payment_method_enabled_label)?.value || false;
    const is_iban_refund_enabled =
      this.company_settings_form_group.get(this.form_service.is_iban_refund_enabled_label)?.value || false;
    const is_ext_order_id_enabled =
      this.company_settings_form_group.get(this.form_service.is_ext_order_id_enabled_label)?.value || false;
    const is_tax_deduction_enabled =
      this.company_settings_form_group.get(this.form_service.is_tax_deduction_enabled_label)?.value || false;
    const is_tax_date_and_number_enabled =
      this.company_settings_form_group.get(this.form_service.is_tax_date_and_number_enabled_label)?.value || false;
    const allowed_deduction_types = this.company_settings_form_group.get(
      this.form_service.tax_deduction_type_label,
    )?.value;
    const is_custom_domains_enabled =
      this.company_settings_form_group.get(this.form_service.is_custom_domains_enabled_label)?.value || false;
    const is_prepaid_cards_blocked =
      this.company_settings_form_group.get(this.form_service.is_prepaid_cards_blocked_label)?.value || false;
    const is_prepaid_cards_blocked_on_long =
      this.company_settings_form_group.get(this.form_service.is_prepaid_cards_blocked_on_long_label)?.value || false;
    const is_prepaid_cards_blocked_on_ecommerce =
      this.company_settings_form_group.get(this.form_service.is_prepaid_cards_blocked_on_ecommerce_label)?.value ||
      false;
    const is_fourthline_onboarding_enabled =
      this.company_settings_form_group.get(this.form_service.is_fourthline_onboarding_enabled_label)?.value || false;

    this.editSettings(this.company?.company_id as string, {
      is_store_payment_method_enabled,
      is_iban_refund_enabled,
      is_ext_order_id_enabled,
      is_tax_deduction_enabled,
      is_tax_date_and_number_enabled,
      allowed_deduction_types,
      is_custom_domains_enabled,
      is_prepaid_cards_blocked,
      is_prepaid_cards_blocked_on_long,
      is_prepaid_cards_blocked_on_ecommerce,
      is_fourthline_onboarding_enabled,
    });
  }

  editSettings(company_id: string, settings: CompanySettings): void {
    this.is_company_edit_running = true;
    this.companies_service.editSettings(company_id, settings).subscribe({
      next: () => {
        this.dialog_ref.close({ success: true });
        this.is_company_edit_running = false;
      },
      error: error => {
        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        this.dialog_ref.close({ error: processed_error });
        this.is_company_edit_running = false;
      },
    });
  }

  isTaxDeductionEnabled(): boolean {
    return this.company_settings_form_group.get(this.form_service.is_tax_deduction_enabled_label)?.value || false;
  }

  isSelectedDeductionTypesEmpty(): boolean {
    const is_tax_deduction_enabled = this.isTaxDeductionEnabled();
    const selected_deduction_types = this.company_settings_form_group.get(
      this.form_service.tax_deduction_type_label,
    )?.value;
    return is_tax_deduction_enabled && (!selected_deduction_types || selected_deduction_types.length === 0);
  }

  onPrepaidBlockToggle(): void {
    const is_blocked = this.company_settings_form_group.get(this.form_service.is_prepaid_cards_blocked_label)?.value;
    const long = this.company_settings_form_group.get(this.form_service.is_prepaid_cards_blocked_on_long_label);
    const ecommerce = this.company_settings_form_group.get(
      this.form_service.is_prepaid_cards_blocked_on_ecommerce_label,
    );

    if (is_blocked) {
      long?.setValue(false);
      ecommerce?.setValue(false);
      long?.disable();
      ecommerce?.disable();
    } else {
      long?.enable();
      ecommerce?.enable();
    }
  }

  isPrepaidBlockEnabled(): boolean {
    return this.company_settings_form_group.get(this.form_service.is_prepaid_cards_blocked_label)?.value || false;
  }

  isTaxDeductionDependencyError(): boolean {
    const tax_eabled = this.company_settings_form_group.get(this.form_service.is_tax_deduction_enabled_label)?.value;

    const ext_id_enabled = this.company_settings_form_group.get(this.form_service.is_ext_order_id_enabled_label)?.value;

    const tax_date_enabled = this.company_settings_form_group.get(
      this.form_service.is_tax_date_and_number_enabled_label,
    )?.value;

    if (tax_eabled && !ext_id_enabled && !tax_date_enabled) {
      return true;
    }

    return false;
  }
}
